import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  PointElement,
  LineElement,
  LineController,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { ChartDataset } from "./models";
import { externalTooltipHandler } from "./elements/tooltip2";
import { htmlLegendPlugin } from "./elements/legend";
import { DeviceConfiguration } from "models/devices/configuration";
import { t } from "i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  PointElement,
  LineElement,
  LineController
);

const BarGraph = (props: {
  id?: string;
  data: ChartDataset;
  config?: DeviceConfiguration;
  stacked?: boolean;
  extra?: string;
  height?: number;
}) => {
  const { id, data, config, stacked, extra, height } = props;

  const hasRightAxis = data.datasets.some((d: any) => d.yAxisID === "y1");

  const unitForAxis = (axis: "y" | "y1") => {
    const match = data.datasets.find((d: any) => (d.yAxisID ?? "y") === axis);
    if (match?.unit) return match.unit;
    if (config?.units) return t("device.units", { context: config.units });
    return "";
  };

  const options = {
    indexAxis: "x" as const,
    responsive: true,
    maintainAspectRatio: false,
    config: config,
    elements: {
      bar: {
        borderWidth: 1,
      },
    },
    plugins: {
      legend: {
        display: false,
        position: "top" as const,
      },
      title: {
        display: false,
        text: "",
      },
      tooltip: {
        enabled: false,
        position: "nearest" as const,
        external: externalTooltipHandler,
      },
      htmlLegend: {
        containerID: "legend-container",
      },
    },

    scales: {
      x: {
        stacked: stacked,
        beginAtZero: true,
        max: 31,
        grid: {
          color: "transparent",
        },
      },
      y: {
        weight: 10,
        border: {
          display: false,
          dash: [5, 5],
        },
        grid: {
          drawBorder: false,
          color: "#cccccc",
        },
        ticks: {
          callback: function (value: number | string) {
            return `${value} ${unitForAxis("y")}`;
          },
          maxTicksLimit: 5,
        },
      },
      ...(hasRightAxis
        ? {
            y1: {
              position: "right" as const,
              weight: 10,
              border: {
                display: false,
                dash: [5, 5],
              },
              grid: {
                drawOnChartArea: false,
                drawBorder: false,
                color: "#cccccc",
              },
              ticks: {
                callback: function (value: number | string) {
                  return `${value} ${unitForAxis("y1")}`;
                },
                maxTicksLimit: 5,
              },
            },
          }
        : {}),
    },
  };

  return (
    <div className="w-full" style={{ height: height - 150 }}>
      <div id="legend-container" className="mb-2" />
      <Bar
        id={id}
        options={options}
        data={data as any}
        plugins={[htmlLegendPlugin]}
        className={`w-full ${extra}`}
      />
    </div>
  );
};

export default BarGraph;
